import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/types/product';
import { toast } from 'sonner';

interface FavoritesContextType {
  favorites: Product[];
  favoriteIds: Set<string>;
  isLoading: boolean;
  addToFavorites: (product: Product) => Promise<void>;
  removeFromFavorites: (productId: string) => Promise<void>;
  toggleFavorite: (product: Product) => Promise<void>;
  isFavorite: (productId: string) => boolean;
  refreshFavorites: () => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

const FAVORITES_STORAGE_KEY = 'fermerchel_favorites';

export const FavoritesProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<Product[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Load favorites from localStorage for guests
  const loadFromLocalStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Product[];
        setFavorites(parsed);
        setFavoriteIds(new Set(parsed.map(p => p.id)));
      }
    } catch (error) {
      console.error('[Favorites] Error loading from localStorage:', error);
    }
  }, []);

  // Save to localStorage for guests
  const saveToLocalStorage = useCallback((items: Product[]) => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('[Favorites] Error saving to localStorage:', error);
    }
  }, []);

  // Load favorites from database for authenticated users
  const loadFromDb = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { data: favoritesData, error } = await supabase
        .from('favorites')
        .select('product_id')
        .eq('user_id', user.id);

      if (error) {
        console.error('[Favorites] Error loading favorites:', error);
        return;
      }

      if (!favoritesData || favoritesData.length === 0) {
        setFavorites([]);
        setFavoriteIds(new Set());
        return;
      }

      // Fetch product details
      const productIds = favoritesData.map(f => f.product_id);
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('*')
        .in('id', productIds);

      if (productsError) {
        console.error('[Favorites] Error loading products:', productsError);
        return;
      }

      
      setFavorites(products || []);
      setFavoriteIds(new Set(products?.map(p => p.id) || []));
    } catch (error) {
      console.error('[Favorites] Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Sync local favorites to DB when user logs in
  const syncLocalToDb = useCallback(async () => {
    if (!user) return;

    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      if (!stored) return;

      const localFavorites = JSON.parse(stored) as Product[];
      if (localFavorites.length === 0) return;

      

      // Insert local favorites (ignore duplicates)
      for (const product of localFavorites) {
        await supabase
          .from('favorites')
          .upsert(
            { user_id: user.id, product_id: product.id },
            { onConflict: 'user_id,product_id' }
          );
      }

      // Clear localStorage after sync
      localStorage.removeItem(FAVORITES_STORAGE_KEY);
    } catch (error) {
      console.error('[Favorites] Error syncing to DB:', error);
    }
  }, [user]);

  // Initialize favorites
  useEffect(() => {
    if (user) {
      syncLocalToDb().then(() => loadFromDb());
    } else {
      loadFromLocalStorage();
    }
  }, [user, syncLocalToDb, loadFromDb, loadFromLocalStorage]);

  // Add to favorites
  const addToFavorites = useCallback(async (product: Product) => {
    if (favoriteIds.has(product.id)) return;

    // Optimistic update
    setFavorites(prev => [...prev, product]);
    setFavoriteIds(prev => new Set([...prev, product.id]));

    if (user) {
      try {
        const { error } = await supabase
          .from('favorites')
          .insert({ user_id: user.id, product_id: product.id });

        if (error) {
          console.error('[Favorites] Error adding:', error);
          // Rollback on error
          setFavorites(prev => prev.filter(p => p.id !== product.id));
          setFavoriteIds(prev => {
            const next = new Set(prev);
            next.delete(product.id);
            return next;
          });
        }
      } catch (error) {
        console.error('[Favorites] Error:', error);
      }
    } else {
      // Save to localStorage for guests
      const updated = [...favorites, product];
      saveToLocalStorage(updated);
    }
  }, [user, favoriteIds, favorites, saveToLocalStorage]);

  // Remove from favorites
  const removeFromFavorites = useCallback(async (productId: string) => {
    if (!favoriteIds.has(productId)) return;

    // Optimistic update
    const removedProduct = favorites.find(p => p.id === productId);
    setFavorites(prev => prev.filter(p => p.id !== productId));
    setFavoriteIds(prev => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });

    if (user) {
      try {
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', productId);

        if (error) {
          console.error('[Favorites] Error removing:', error);
          // Rollback on error
          if (removedProduct) {
            setFavorites(prev => [...prev, removedProduct]);
            setFavoriteIds(prev => new Set([...prev, productId]));
          }
        }
      } catch (error) {
        console.error('[Favorites] Error:', error);
      }
    } else {
      // Save to localStorage for guests
      const updated = favorites.filter(p => p.id !== productId);
      saveToLocalStorage(updated);
    }
  }, [user, favoriteIds, favorites, saveToLocalStorage]);

  // Toggle favorite with toast notification
  const toggleFavorite = useCallback(async (product: Product) => {
    const wasInFavorites = favoriteIds.has(product.id);
    
    if (wasInFavorites) {
      await removeFromFavorites(product.id);
      toast.success('Удалено из избранного', {
        description: product.name,
        icon: '💔',
      });
    } else {
      await addToFavorites(product);
      toast.success('Добавлено в избранное', {
        description: product.name,
        icon: '❤️',
      });
    }
  }, [favoriteIds, addToFavorites, removeFromFavorites]);

  // Check if product is favorite
  const isFavorite = useCallback((productId: string) => {
    return favoriteIds.has(productId);
  }, [favoriteIds]);

  // Refresh favorites
  const refreshFavorites = useCallback(async () => {
    if (user) {
      await loadFromDb();
    } else {
      loadFromLocalStorage();
    }
  }, [user, loadFromDb, loadFromLocalStorage]);

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        favoriteIds,
        isLoading,
        addToFavorites,
        removeFromFavorites,
        toggleFavorite,
        isFavorite,
        refreshFavorites,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
